import React, { useState } from 'react';
import { usePublications } from '../services/publicationsService.js';
import { useAppData } from '../context/AppDataContext.jsx';
import FileTypeIcon from '../components/shared/FileTypeIcon.jsx';
import './RecentPublications.scss';

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Effective publication date: explicit publish date if set, otherwise the blob's last-modified timestamp.
function pubDate(pub) {
  return pub.metadata?.publishdate || pub.lastModified || null;
}

// Name used for display and search: the title if set, otherwise the file name.
function pubName(pub) {
  return pub.metadata?.title || pub.name || '';
}

const RecentPublications = () => {
  const { data: pubs, loading, error } = usePublications();
  const { me } = useAppData();
  const [search, setSearch] = useState('');

  // Use customer's publicationAccess directly (no outlook mapping needed)
  const allowedTags = React.useMemo(() => {
    return new Set(me?.publicationAccess || []);
  }, [me?.publicationAccess]);

  // Access-filtered list, sorted newest first.
  const visible = React.useMemo(() => {
    if (!pubs) return [];
    const accessible = pubs.filter(pub => {
      const pubOutlooks = pub.metadata?.outlooks;
      if (!pubOutlooks || pubOutlooks.length === 0) return false;
      const arr = Array.isArray(pubOutlooks) ? pubOutlooks : [pubOutlooks];
      return arr.some(tag => allowedTags.has(tag));
    });
    return [...accessible].sort((a, b) => {
      const da = pubDate(a) ? new Date(pubDate(a)) : 0;
      const db = pubDate(b) ? new Date(pubDate(b)) : 0;
      return db - da;
    });
  }, [pubs, allowedTags]);

  // Apply the name search on top of the access-filtered list.
  const displayed = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter(pub =>
      pubName(pub).toLowerCase().includes(q) || (pub.name || '').toLowerCase().includes(q)
    );
  }, [visible, search]);

  return (
    <div className="recent-publications-page">
      <h1>Recent Publications</h1>
      <section className="recent-publications-list">
        <div className="publications-header">
          <h2>All Publications</h2>
          <input
            type="search"
            className="publications-search"
            placeholder="Search by name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search publications by name"
          />
        </div>
        {loading && <p className="publications-empty">Loading publications…</p>}
        {error && <p className="publications-empty" style={{ color: 'red' }}>{error}</p>}
        {!loading && !error && visible.length === 0 && (
          <p className="publications-empty">No publications available.</p>
        )}
        {!loading && !error && visible.length > 0 && displayed.length === 0 && (
          <p className="publications-empty">No publications match your search.</p>
        )}
        {!loading && !error && displayed.length > 0 && (
          <table className="publications-table">
            <thead>
              <tr>
                <th className="col-name">Publication</th>
                <th className="col-date">Date</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((pub, idx) => (
                <tr key={pub.name + idx}>
                  <td className="col-name">
                    <div className="pub-title">
                      <a href={pub.sasUrl} download target="_blank" rel="noopener noreferrer">
                        <FileTypeIcon fileName={pub.name} />
                        {' '}
                        {pubName(pub)}
                      </a>
                    </div>
                    {pub.metadata?.description && (
                      <div className="pub-description">{pub.metadata.description}</div>
                    )}
                  </td>
                  <td className="col-date">{formatDate(pubDate(pub)) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default RecentPublications;
